import { EmployerSignupStatus } from '@zorro/clients';
import { Modal } from '@zorro/design-system/v2/Modal';
import {
  showSuccessNotification,
  useAppRouter,
  useIdempotencyKey,
  useMonolithMutation,
  useMonolithQuery,
  useRoles,
} from '@zorro/web/ui-utils';
import { useState } from 'react';

import { useCompanyDetailsForm } from '../../CompanyDetailsForm';
import { useCompanySetupForm } from '../../CompanySetupForm';
import { useContactsForm } from '../../ContactsForm';
import { usePlanYearSetupForm } from '../../PlanYearSetupForm';
import { useZorroSetupForm } from '../../ZorroSetupForm';
import { getFormStatusesConfig } from '../AddEmployerFlowUtils';
import {
  AddEmployerStep,
  AddEmployerStepType,
  HandleStepSubmit,
} from '../AddEmployerStep';
import { CancelInviteForm } from './CancelInviteForm';
import { CompanyDetailsCard } from './Cards/CompanyDetailsCard';
import { CompanySetupCard } from './Cards/CompanySetupCard';
import { ContactsCard } from './Cards/ContactsCard';
import { PlanYearSetupCard } from './Cards/PlanYearSetupCard';
import { ZorroSetupCard } from './Cards/ZorroSetupCard';
import { buildCreatePaymentsEmployerDto } from './ReviewForm.utils';
import { ReviewStatus } from './ReviewStatus';
import { SendInviteForm } from './SendInviteForm/SendInviteForm';
import { useReviewFormFinalization } from './useReviewFormFinalization';

const isDon = false;
const isSignedAgreementVisible = true;

type Props = {
  employerId: string;
  isFinalizationMode?: boolean;
};

export const ReviewForm = ({
  employerId,
  isFinalizationMode = false,
}: Props) => {
  'use no memo';
  const { isZorroOperations } = useRoles();
  const { navigateToRoute } = useAppRouter();
  const [isSendInviteModalOpen, setIsSendInviteModalOpen] =
    useState<boolean>(false);
  const [isCancelInviteModalOpen, setIsCancelInviteModalOpen] =
    useState<boolean>(false);
  const idempotencyKey = useIdempotencyKey();

  const { mutate: mutateEmployer } = useMonolithMutation({
    method: 'employersControllerUpdate',
  });
  const { mutate: mutateCreatePaymentsEmployer } = useMonolithMutation({
    method: 'paymentsControllerCreateEmployer',
  });
  const { mutate: mutateActivateContacts } = useMonolithMutation({
    method: 'employersControllerActivateContacts',
    shouldInvalidateQueries: false,
  });
  const { data: employer } = useMonolithQuery({
    method: 'employersControllerFindOne',
    params: employerId && [employerId],
  });

  const {
    companyDetailsForm,
    isSignedAgreementRequired,
    isLynxPaymentsEnabled,
  } = useCompanyDetailsForm({
    employerId,
    isFinalizationMode,
    isSignedAgreementVisible,
    isDon,
  });
  const { contactsForm } = useContactsForm(employerId, isFinalizationMode);
  const { companySetupForm } = useCompanySetupForm(
    employerId,
    isFinalizationMode
  );
  const { zorroSetupForm, allAgencies } = useZorroSetupForm({ employerId });
  const { planYearSetupForm, isAllowanceModelRequired } = usePlanYearSetupForm({
    shouldFallbackToEarliestPeriod: true,
    isFinalizationMode,
    employerId,
  });

  const { isFinalizationModeValid, shouldHideActivationButtons } =
    useReviewFormFinalization({
      employerStatus: employer?.employerStatus,
      isFinalizationMode,
      employerId,
    });

  const {
    submitLabel = null,
    successMessage,
    nextSignupStatus,
    navigateTo,
    isZorroOnly,
    shouldShowSkipInvitation,
    shouldActivateEmployerAdmin,
  } = getFormStatusesConfig(employer?.signupStatus) || {};

  const isReviewCompleted =
    isLynxPaymentsEnabled &&
    employer?.signupStatus === EmployerSignupStatus.IN_ZORRO_SETUP;

  const isSubmitDisabled =
    !companyDetailsForm.formState.isValid ||
    !contactsForm.formState.isValid ||
    !companySetupForm.formState.isValid ||
    !zorroSetupForm.formState.isValid ||
    !planYearSetupForm.formState.isValid ||
    (isZorroOnly && !isZorroOperations);

  const createEmployerPaymentsAccount = async (): Promise<void> => {
    const { reserveAmount, thresholdAmount } = planYearSetupForm.getValues();
    if (!reserveAmount || !thresholdAmount) {
      throw new Error(
        'Reserve amount and threshold amount are required to create the employer payments account'
      );
    }

    const createPaymentsEmployerDto = buildCreatePaymentsEmployerDto(
      employerId,
      companyDetailsForm.getValues(),
      reserveAmount,
      thresholdAmount
    );
    await mutateCreatePaymentsEmployer([
      idempotencyKey.get(),
      createPaymentsEmployerDto,
    ]);
  };

  const handleSubmit: HandleStepSubmit = async () => {
    if (isReviewCompleted) {
      return { navigate: true, employerId };
    }

    if (nextSignupStatus && successMessage) {
      try {
        if (shouldActivateEmployerAdmin) {
          await mutateActivateContacts([employerId]);
        }

        if (
          nextSignupStatus === EmployerSignupStatus.IN_ZORRO_SETUP &&
          isLynxPaymentsEnabled
        ) {
          await createEmployerPaymentsAccount();
        }

        await mutateEmployer([
          employerId,
          {
            zorroPartnerId: employer?.zorroPartnerId || null,
            signupStatus: nextSignupStatus,
          },
          idempotencyKey.get(),
        ]);

        showSuccessNotification({ message: successMessage });
        if (navigateTo) {
          navigateToRoute(navigateTo, [employerId]);
        } else {
          return { navigate: true, employerId };
        }
      } catch {
        /* empty */
      }
    } else {
      setIsSendInviteModalOpen(true);
    }

    return { navigate: false };
  };

  const handleSkipInvitation: HandleStepSubmit = async () => {
    try {
      const skipConfig = getFormStatusesConfig(
        EmployerSignupStatus.NEEDS_REVIEW
      );
      if (!skipConfig) {
        return { navigate: false };
      }

      if (isLynxPaymentsEnabled) {
        await createEmployerPaymentsAccount();
      }

      await mutateEmployer([
        employerId,
        {
          zorroPartnerId: employer?.zorroPartnerId || null,
          signupStatus: skipConfig.nextSignupStatus,
        },
        idempotencyKey.get(),
      ]);

      showSuccessNotification({ message: skipConfig.successMessage });
      return { navigate: true, employerId };
    } catch {
      return { navigate: false };
    }
  };

  const employerName = employer?.name || '';

  return (
    <AddEmployerStep
      isSubmitDisabled={isSubmitDisabled}
      isFinalizationMode={isFinalizationMode}
      step={AddEmployerStepType.REVIEW}
      hasNextPaymentsStep={isZorroOperations && isLynxPaymentsEnabled}
      title="Review employer info"
      submitLabel={isReviewCompleted ? undefined : submitLabel}
      employerId={employerId}
      onSubmit={handleSubmit}
      secondaryLabel={
        shouldShowSkipInvitation && isFinalizationModeValid
          ? 'Skip invitation and finalize'
          : undefined
      }
      onSecondarySubmit={handleSkipInvitation}
      paperProps={shouldHideActivationButtons ? { display: 'none' } : undefined}
      isDon={false}
    >
      {employer && (
        <ReviewStatus
          employer={employer}
          setIsCancelInviteModalOpen={setIsCancelInviteModalOpen}
        />
      )}
      <CompanyDetailsCard
        isSignedAgreementRequired={isSignedAgreementRequired}
        isFinalizationMode={isFinalizationMode}
        isValid={companyDetailsForm.formState.isValid}
        fields={companyDetailsForm.watch()}
        employerId={employerId}
        isDon={isDon}
        isLynxPaymentsEnabled={isLynxPaymentsEnabled}
      />
      <ContactsCard
        isFinalizationMode={isFinalizationMode}
        isValid={contactsForm.formState.isValid}
        fields={contactsForm.watch()}
        employerId={employerId}
        isDon={isDon}
      />
      <CompanySetupCard
        isFinalizationMode={isFinalizationMode}
        isValid={companySetupForm.formState.isValid}
        fields={companySetupForm.watch()}
        employerId={employerId}
        isDon={isDon}
      />
      <ZorroSetupCard
        isValid={zorroSetupForm.formState.isValid}
        zorroSetupForm={zorroSetupForm}
        allAgencies={allAgencies}
        employerId={employerId}
        isDon={isDon}
      />
      <PlanYearSetupCard
        isFinalizationMode={isFinalizationMode}
        isAllowanceModelRequired={isAllowanceModelRequired}
        isValid={planYearSetupForm.formState.isValid}
        fields={planYearSetupForm.watch()}
        employerName={employerName}
        employerId={employerId}
        isDon={isDon}
        isLynxPaymentsEnabled={isLynxPaymentsEnabled}
      />
      <Modal
        isOpen={isCancelInviteModalOpen}
        onClose={() => setIsCancelInviteModalOpen(false)}
        title="Cancel Invitation?"
      >
        <CancelInviteForm
          onClose={() => setIsCancelInviteModalOpen(false)}
          employerId={employerId}
          employerName={employerName}
        />
      </Modal>
      <Modal
        isOpen={isSendInviteModalOpen}
        onClose={() => setIsSendInviteModalOpen(false)}
        size={906}
        hasNoPadding
      >
        <SendInviteForm
          onClose={() => setIsSendInviteModalOpen(false)}
          employerName={employerName}
          employerId={employerId}
        />
      </Modal>
    </AddEmployerStep>
  );
};
